import { Component } from "../components/component";
import { ValidationError } from "@se/platform";
import { DataType } from "@se/platform/forms/enums/dataType";
import { ResponseDto } from "@se/platform/forms/dtos/out/responses/responseDto";

import { BehaviorSubject, Observable } from "rxjs";
import { deepCopy } from "../utils";
import { Form } from "./form";
import { first, skip } from "rxjs/operators";
import { IMessagingService } from "../services";

abstract class FormFieldComponent extends Component {

    protected labelEl: HTMLLabelElement;
    protected helpTextEl: HTMLElement;
    protected helpButtonEl: HTMLButtonElement;
    protected errorMessageEl: HTMLElement;
    protected errorMessageCustomEl: HTMLElement;

    public fieldId: string;
    public fieldDataType: DataType;
    public formId: string;
    public fieldInputElWithValidState: HTMLElement;
    public errorMessageCustom: string;
    protected response$: BehaviorSubject<ResponseDto>;
    
     constructor(protected fieldEl: HTMLElement, messagingService: IMessagingService, protected form: Form) {
        super(fieldEl, messagingService);

        this.fieldId = fieldEl.dataset["fieldId"];
        this.fieldDataType = DataType[fieldEl.dataset["fieldDataType"]];
        this.formId = fieldEl.closest<HTMLElement>("[data-form-id]").dataset["formId"];
        this.fieldInputElWithValidState = fieldEl.querySelector<HTMLElement>(`[aria-invalid]`);

        this.helpTextEl = fieldEl.querySelector<HTMLElement>(".js-help-text");
        this.helpButtonEl = fieldEl.querySelector<HTMLButtonElement>(".js-help-button");
        this.errorMessageEl = fieldEl.querySelector<HTMLElement>(".js-error-message");
        this.errorMessageCustomEl = fieldEl.querySelector<HTMLElement>("[data-error-message]");
        this.errorMessageCustom = this.errorMessageCustomEl ? this.errorMessageCustomEl.dataset.errorMessage : "";
        
        const defaultResponse = new ResponseDto();
        defaultResponse.fieldId = this.fieldId;
        defaultResponse.dataType = this.fieldDataType;
        defaultResponse.value = null;
        this.response$ = new BehaviorSubject<ResponseDto>(defaultResponse);

        // remove any active validation errors from this field when the form is submitted, or when the value changes
        this.form.onSubmitting$().subscribe(() => this.hideError());
        this.onValueChanged$().subscribe(() => this.hideError());

        // listen for validation errors for this field
        this.form.onValidationError$(this.fieldId).subscribe(error => this.showError(error));

        // if a response is set from the form for this field, update its display value to match
        const onResponseLoaded$ = this.form.onValueChanged$(this.fieldId).pipe(first());
        onResponseLoaded$.subscribe(response => { this.setDisplayValue(response.value);});
    }
    
    public onValueChanged$(): Observable<ResponseDto> {
        // the first value will always be the default null value, so we can skip that
        return this.response$.pipe(skip(1));
    }

    // get the current response value of the field.
    public getValue() : ResponseDto {
        return this.response$.value;
    }

    // public method to set the value for this field.
    public setValue(value: any) {
        this.setDisplayValue(value);
        this.setResponseValue(value);
    }

    // internal method to update the underlying response of this field.
    protected setResponseValue(value: any) {

        const response = deepCopy(this.response$.value);
        response.value = value;
        this.response$.next(response);
    }

    // internal method to update the value of this field in the DOM.
    protected abstract setDisplayValue(value: any);

    // displays an error message against the field, and sets its invalid state.
    public showError(error: ValidationError) {
        this.fieldEl.classList.add("form-field--invalid");
        this.fieldInputElWithValidState?.setAttribute("aria-invalid","true");
        this.errorMessageEl.innerText = error.errorMessage;
    }

    // hides any error messages from the field.
    public hideError() {
        this.fieldEl.classList.remove("form-field--invalid");
        this.fieldInputElWithValidState?.setAttribute("aria-invalid","false");
        if (this.errorMessageEl) {
            this.errorMessageEl.innerText = "";
        }
    }

    public abstract setFocus();
}

export { FormFieldComponent };
